import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import BOLayout from '@/pages/back-office/BOLayout';
import TopBanner from '@/components/back-office/TopBanner';
import { toast } from 'react-toastify';
import axios from 'axios';
import DeleteRestaurantModal from '@/components/DeleteRestaurantModal';
import { useRestaurantsContext } from '@/contexts/RestaurantContext';

const BORestaurantHome = () => {
	const { restaurantId } = useParams();
	const navigate = useNavigate();
	const { selectedRestaurant, refreshSelectedRestaurant } = useRestaurantsContext();
	const [isHaguiModeEnabled, setIsHaguiModeEnabled] = useState(selectedRestaurant.is_haguim_mode);
	const [isDeliveryEnabled, setIsDeliveryEnabled] = useState(selectedRestaurant.is_delivery_enabled);
	const [isShabbatModeEnabled, setIsShabbatModeEnabled] = useState(selectedRestaurant.is_shabbat_mode);
	const [isRecurringModeEnabled, setIsRecurringModeEnabled] = useState(selectedRestaurant.is_recurring_mode);
	const [deliveryDates, setDeliveryDates] = useState(selectedRestaurant.delivery_dates || []);
	const [newDeliveryDate, setNewDeliveryDate] = useState('');
	const [showDeliveryDates, setShowDeliveryDates] = useState(false);
	const [recurringDays, setRecurringDays] = useState(selectedRestaurant.recurring_days || []);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const daysOfWeek = [
		{ id: 0, name: 'Dimanche' },
		{ id: 1, name: 'Lundi' },
		{ id: 2, name: 'Mardi' },
		{ id: 3, name: 'Mercredi' },
		{ id: 4, name: 'Jeudi' },
		{ id: 5, name: 'Vendredi' },
		{ id: 6, name: 'Samedi' }
	];

	const handleHaguimModeToggle = async () => {
		try {
			const response = await axios.patch(
				`${import.meta.env.VITE_API_URL}/api/back-office/restaurants/${restaurantId}/haguim-mode`,
				{ is_haguim_mode: !isHaguiModeEnabled }
			);

			if (response.status === 200) {
				setIsHaguiModeEnabled(!isHaguiModeEnabled);
				toast.success('Mode Haguim mis à jour avec succès');
			}
		} catch (error) {
			console.error('Erreur lors de la mise à jour du mode Haguim:', error);
			toast.error('Erreur lors de la mise à jour du mode Haguim');
		}
	};

	const handleDeliveryOptionToggle = async () => {
		try {
			const url = `${import.meta.env.VITE_API_URL}/api/back-office/restaurants/${restaurantId}/delivery-option`;
			const data = {
				is_delivery_enabled: !isDeliveryEnabled,
				delivery_dates: !isDeliveryEnabled ? deliveryDates : [],
				is_recurring_mode: !isDeliveryEnabled ? isRecurringModeEnabled : false,
				is_shabbat_mode: !isDeliveryEnabled ? isShabbatModeEnabled : false,
				recurring_days: !isDeliveryEnabled ? recurringDays : []
			};
			console.log('[FRONT] PATCH', url, data);
			const response = await axios.patch(url, data);
			console.log('[FRONT] Réponse:', response);

			if (response.status === 200) {
				setIsDeliveryEnabled(!isDeliveryEnabled);
				if (isDeliveryEnabled) {
					setDeliveryDates([]);
					setIsRecurringModeEnabled(false);
					setIsShabbatModeEnabled(false);
					setRecurringDays([]);
				}
				await refreshSelectedRestaurant();
				toast.success('Option de livraison mise à jour avec succès');
			}
		} catch (error) {
			console.error('[FRONT] Erreur PATCH option livraison:', error);
			toast.error('Erreur lors de la mise à jour de l\'option de livraison');
		}
	};

	const handleShabbatModeToggle = async () => {
		try {
			const url = `${import.meta.env.VITE_API_URL}/api/back-office/restaurants/${restaurantId}/delivery-option`;
			const data = {
				is_delivery_enabled: isDeliveryEnabled,
				is_shabbat_mode: !isShabbatModeEnabled,
				is_recurring_mode: false,
				delivery_dates: deliveryDates
			};
			console.log('[FRONT] PATCH', url, data);
			const response = await axios.patch(url, data);
			console.log('[FRONT] Réponse:', response);

			if (response.status === 200) {
				setIsShabbatModeEnabled(!isShabbatModeEnabled);
				setIsRecurringModeEnabled(false);
				toast.success('Mode Shabbat mis à jour avec succès');
			}
		} catch (error) {
			console.error('[FRONT] Erreur PATCH mode Shabbat:', error);
			toast.error('Erreur lors de la mise à jour du mode Shabbat');
		}
	};

	const handleRecurringModeToggle = async () => {
		try {
			const url = `${import.meta.env.VITE_API_URL}/api/back-office/restaurants/${restaurantId}/delivery-option`;
			const data = {
				is_delivery_enabled: isDeliveryEnabled,
				is_recurring_mode: !isRecurringModeEnabled,
				is_shabbat_mode: false,
				recurring_days: recurringDays,
				delivery_dates: deliveryDates
			};
			console.log('[FRONT] PATCH', url, data);
			const response = await axios.patch(url, data);
			console.log('[FRONT] Réponse:', response);

			if (response.status === 200) {
				setIsRecurringModeEnabled(!isRecurringModeEnabled);
				setIsShabbatModeEnabled(false);
				await refreshSelectedRestaurant();
				toast.success('Mode récurrent mis à jour avec succès');
			}
		} catch (error) {
			console.error('[FRONT] Erreur PATCH mode récurrent:', error);
			toast.error('Erreur lors de la mise à jour du mode récurrent');
		}
	};

	const handleRecurringDayToggle = async (dayId) => {
		let updatedRecurringDays;
		if (recurringDays.includes(dayId)) {
			updatedRecurringDays = recurringDays.filter(day => day !== dayId);
		} else {
			updatedRecurringDays = [...recurringDays, dayId];
		}

		setRecurringDays(updatedRecurringDays);

		try {
			const url = `${import.meta.env.VITE_API_URL}/api/back-office/restaurants/${restaurantId}/delivery-option`;
			const data = {
				is_delivery_enabled: isDeliveryEnabled,
				is_recurring_mode: isRecurringModeEnabled,
				recurring_days: updatedRecurringDays,
				delivery_dates: deliveryDates
			};
			console.log('[FRONT] PATCH', url, data);
			const response = await axios.patch(url, data);
			console.log('[FRONT] Réponse:', response);

			if (response.status === 200) {
				await refreshSelectedRestaurant();
				toast.success('Jours récurrents mis à jour avec succès');
			}
		} catch (error) {
			console.error('[FRONT] Erreur PATCH jours récurrents:', error);
			toast.error('Erreur lors de la mise à jour des jours récurrents');
			setRecurringDays(recurringDays);
		}
	};

	const handleAddDeliveryDate = async () => {
		if (!newDeliveryDate) {
			toast.error('Veuillez sélectionner une date');
			return;
		}

		if (deliveryDates.includes(newDeliveryDate)) {
			toast.error('Cette date est déjà dans la liste');
			return;
		}

		const updatedDates = [...deliveryDates, newDeliveryDate];

		try {
			const url = `${import.meta.env.VITE_API_URL}/api/back-office/restaurants/${restaurantId}/delivery-option`;
			const data = {
				is_delivery_enabled: isDeliveryEnabled,
				delivery_dates: updatedDates,
				is_recurring_mode: isRecurringModeEnabled,
				is_shabbat_mode: isShabbatModeEnabled
			};
			console.log('[FRONT] PATCH', url, data);
			const response = await axios.patch(url, data);
			console.log('[FRONT] Réponse:', response);

			if (response.status === 200) {
				setDeliveryDates(updatedDates);
				setNewDeliveryDate('');
				await refreshSelectedRestaurant();
				toast.success('Date de livraison ajoutée avec succès');
			}
		} catch (error) {
			console.error('[FRONT] Erreur PATCH ajout date livraison:', error);
			toast.error('Erreur lors de l\'ajout de la date de livraison');
		}
	};

	const handleRemoveDeliveryDate = async (dateToRemove) => {
		const updatedDates = deliveryDates.filter(date => date !== dateToRemove);

		try {
			const url = `${import.meta.env.VITE_API_URL}/api/back-office/restaurants/${restaurantId}/delivery-option`;
			const data = {
				is_delivery_enabled: isDeliveryEnabled,
				delivery_dates: updatedDates,
				is_recurring_mode: isRecurringModeEnabled,
				is_shabbat_mode: isShabbatModeEnabled
			};
			console.log('[FRONT] PATCH', url, data);
			const response = await axios.patch(url, data);
			console.log('[FRONT] Réponse:', response);

			if (response.status === 200) {
				setDeliveryDates(updatedDates);
				await refreshSelectedRestaurant();
				toast.success('Date de livraison supprimée avec succès');
			}
		} catch (error) {
			console.error('[FRONT] Erreur PATCH suppression date livraison:', error);
			toast.error('Erreur lors de la suppression de la date de livraison');
			setDeliveryDates(deliveryDates);
		}
	};

	if (!selectedRestaurant) {
		return <div>Chargement...</div>;
	}

	return (
		<BOLayout>
			<div className="p-6">
				<TopBanner
					role="super-admin"
					showToggle={true}
					navigationRouteText={'Créer un restaurant'}
					navigationRoute={`/back-office/restaurants/creation`}
				/>

				<div className="flex flex-col gap-4">
					<div className="flex items-center justify-between bg-white p-4 rounded-lg shadow">
						<div className="flex items-center gap-2">
							<span className="font-medium">Mode Haguim</span>
							<button
								onClick={handleHaguimModeToggle}
								className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#A2924A] focus:ring-offset-2 ${isHaguiModeEnabled ? 'bg-[#A2924A]' : 'bg-gray-200'
									}`}
							>
								<span
									className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${isHaguiModeEnabled ? 'translate-x-6' : 'translate-x-1'
										}`}
								/>
							</button>
						</div>
						<p className="text-sm text-gray-500">
							{isHaguiModeEnabled ? 'Mode Haguim activé' : 'Mode Haguim désactivé'}
						</p>
					</div>

					<div className="flex items-center justify-between bg-white p-4 rounded-lg shadow">
						<div className="flex items-center gap-2">
							<span className="font-medium">Option de livraison</span>
							<button
								onClick={handleDeliveryOptionToggle}
								className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#A2924A] focus:ring-offset-2 ${isDeliveryEnabled ? 'bg-[#A2924A]' : 'bg-gray-200'
									}`}
							>
								<span
									className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${isDeliveryEnabled ? 'translate-x-6' : 'translate-x-1'
										}`}
								/>
							</button>
						</div>
						<p className="text-sm text-gray-500">
							{isDeliveryEnabled ? 'Option de livraison activée' : 'Option de livraison désactivée'}
						</p>
					</div>

					{isDeliveryEnabled && (
						<div className="flex items-center justify-between bg-white p-4 rounded-lg shadow">
							<div className="flex items-center gap-2">
								<span className="font-medium">Mode Shabbat</span>
								<button
									onClick={handleShabbatModeToggle}
									className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#A2924A] focus:ring-offset-2 ${isShabbatModeEnabled ? 'bg-[#A2924A]' : 'bg-gray-200'
										}`}
								>
									<span
										className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${isShabbatModeEnabled ? 'translate-x-6' : 'translate-x-1'
											}`}
									/>
								</button>
							</div>
							<p className="text-sm text-gray-500">
								{isShabbatModeEnabled ? 'Dates automatiques (jeudi/vendredi)' : 'Dates personnalisées'}
							</p>
						</div>
					)}

					{isDeliveryEnabled && (
						<div className="flex items-center justify-between bg-white p-4 rounded-lg shadow">
							<div className="flex items-center gap-2">
								<span className="font-medium">Mode récurrent</span>
								<button
									onClick={handleRecurringModeToggle}
									className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#A2924A] focus:ring-offset-2 ${isRecurringModeEnabled ? 'bg-[#A2924A]' : 'bg-gray-200'
										}`}
								>
									<span
										className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${isRecurringModeEnabled ? 'translate-x-6' : 'translate-x-1'
											}`}
									/>
								</button>
							</div>
							<p className="text-sm text-gray-500">
								{isRecurringModeEnabled ? 'Jours de semaine sélectionnés' : 'Dates personnalisées'}
							</p>
						</div>
					)}

					{isDeliveryEnabled && isRecurringModeEnabled && (
						<div className="bg-white p-4 rounded-lg shadow">
							<h3 className="font-medium mb-3">Jours de livraison récurrents</h3>
							<div className="grid grid-cols-2 gap-2 md:grid-cols-3 lg:grid-cols-4">
								{daysOfWeek.map(day => (
									<button
										key={day.id}
										onClick={() => handleRecurringDayToggle(day.id)}
										className={`p-2 rounded-md font-medium transition-colors ${recurringDays.includes(day.id)
											? 'bg-[#A2924A] text-white hover:bg-[#8A7B3F]'
											: 'bg-gray-100 text-gray-700 hover:bg-gray-200'
											}`}
									>
										{day.name}
									</button>
								))}
							</div>
							<p className="text-sm text-gray-500 mt-2">
								Sélectionnez les jours de la semaine où la livraison est disponible
							</p>
						</div>
					)}

					{isDeliveryEnabled && !isShabbatModeEnabled && !isRecurringModeEnabled && (
						<div className="bg-white p-4 rounded-lg shadow">
							<div className="flex justify-between items-center">
								<h3 className="font-medium mb-2">Dates de livraison disponibles</h3>
								<button
									onClick={() => setShowDeliveryDates(!showDeliveryDates)}
									className="text-[#A2924A] hover:text-[#8A7B3F]"
								>
									{showDeliveryDates ? 'Masquer' : 'Afficher'}
								</button>
							</div>

							{showDeliveryDates && (
								<>
									<div className="flex gap-2 mb-4">
										<input
											type="date"
											value={newDeliveryDate}
											onChange={(e) => setNewDeliveryDate(e.target.value)}
											className="border rounded px-3 py-2 w-40"
											min={new Date().toISOString().split('T')[0]}
										/>
										<button
											onClick={handleAddDeliveryDate}
											className="bg-[#A2924A] text-white px-4 py-2 rounded hover:bg-[#8A7B3F]"
										>
											Ajouter
										</button>
									</div>

									{deliveryDates.length > 0 ? (
										<ul className="space-y-2">
											{deliveryDates.sort().map((date) => (
												<li key={date} className="flex justify-between items-center bg-gray-100 p-2 rounded">
													<span>{new Date(date).toLocaleDateString()}</span>
													<button
														onClick={() => handleRemoveDeliveryDate(date)}
														className="text-red-500 hover:text-red-700"
													>
														<svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
															<path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
														</svg>
													</button>
												</li>
											))}
										</ul>
									) : (
										<p className="text-gray-500">Aucune date de livraison disponible</p>
									)}
								</>
							)}
						</div>
					)}

					<div className="flex gap-4">
						<button
							onClick={() => navigate(`/back-office/restaurants/${restaurantId}/menu`)}
							className="flex-1 bg-[#A2924A] text-white px-4 py-2 rounded-lg hover:bg-[#8A7B3F] transition-colors"
						>
							Gérer le menu
						</button>
						<button
							onClick={() => navigate(`/back-office/restaurants/${restaurantId}/products`)}
							className="flex-1 bg-[#A2924A] text-white px-4 py-2 rounded-lg hover:bg-[#8A7B3F] transition-colors"
						>
							Gérer les produits
						</button>
					</div>

					<button
						onClick={() => setIsModalOpen(true)}
						className="w-full bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 transition-colors mt-4"
					>
						Supprimer le restaurant
					</button>
				</div>

				<DeleteRestaurantModal
					restaurant={selectedRestaurant}
					isOpen={isModalOpen}
					onClose={() => setIsModalOpen(!isModalOpen)}
				/>
			</div>
		</BOLayout>
	);
};

export default BORestaurantHome;
